import {CommonModule} from '@angular/common';
import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {DomainSetting} from '@gatewee/types/domain-setting';
import {RecipientSetting} from '@gatewee/types/recipient-setting';
import {FeeSnapshot, FeeSnapshotItem} from '@gatewee/types/fee';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';
import {GateweeStyleSettingsComponent} from '../../gatewee-style-settings/gatewee-style-settings.component';
import {GateweePageSettingsComponent} from '../../gatewee-page-settings/gatewee-page-settings.component';
import {GateweeFeesSettingsComponent} from '../../gatewee-fees-settings/gatewee-fees-settings.component';
import {FeeEntity} from '../../gatewee-fees-settings/gatewee-fees-settings.component';

export interface SettingsDialogData {
  id: number;
  setting: RecipientSetting | DomainSetting;
  editSettingFn: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;
  // Optional context for generating component data
  entity?: {
    id: number;
    name: string;
    document: string;
    representative?: {
      name?: string;
      email?: string;
      phone?: string;
    };
  };
  scope?: 'domain' | 'recipient';
  // Optional: if provided, these override auto-detection
  componentType?: Type<unknown>;
  formGroupInitializer?: (
    settingValue: string | Record<string, unknown> | unknown,
    fb: FormBuilder,
  ) => FormGroup;
  additionalInputs?: Record<string, unknown>;
}

@Component({
  selector: 'app-settings-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
  ],
  templateUrl: './settings-dialog.component.html',
})
export class SettingsDialogComponent implements OnInit, AfterViewInit {
  @ViewChild('componentContainer', {read: ViewContainerRef})
  componentContainer!: ViewContainerRef;

  formGroup!: FormGroup;
  submitting = false;
  id!: number;
  setting!: RecipientSetting | DomainSetting;
  editSettingFn!: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;
  componentType!: Type<unknown>;
  formGroupInitializer?: (
    settingValue: string | Record<string, unknown> | unknown,
    fb: FormBuilder,
  ) => FormGroup;
  additionalInputs?: Record<string, unknown>;
  entity?: SettingsDialogData['entity'];
  scope?: SettingsDialogData['scope'];

  showButtons = true;

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, SettingsDialogData>,
  ) {
    const data = this.context.data;
    this.id = data.id;
    this.setting = data.setting;
    this.editSettingFn = data.editSettingFn;
    this.entity = data.entity;
    this.scope = data.scope || 'domain';

    // If component data is already provided, use it; otherwise generate it
    if (data.componentType) {
      this.componentType = data.componentType;
      this.formGroupInitializer = data.formGroupInitializer;
      this.additionalInputs = data.additionalInputs || {};
    } else {
      // Auto-generate component data based on setting key
      const componentData = this.getComponentDataForSetting(
        this.setting.key,
        this.id,
        this.setting,
        this.editSettingFn,
      );
      this.componentType = componentData.componentType;
      this.formGroupInitializer = componentData.formGroupInitializer;
      this.additionalInputs = componentData.additionalInputs || {};

      console.log(this.formGroupInitializer);
    }
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  ngAfterViewInit(): void {
    this.loadComponent();
  }

  private initializeForm(): void {
    if (this.formGroupInitializer) {
      this.formGroup = this.formGroupInitializer(this.setting.value, this.fb);
    } else if (this.componentType) {
      // For components that don't use formGroup (like fees), create an empty form
      // These components handle their own submission via callbacks (e.g., actions.onSubmit)
      this.formGroup = this.fb.group({});
    } else {
      // Default form group initialization
      let parsedValue: Record<string, unknown> = {};

      if (this.setting.value) {
        try {
          const parsed =
            typeof this.setting.value === 'string'
              ? JSON.parse(this.setting.value)
              : this.setting.value;
          parsedValue = parsed as Record<string, unknown>;
        } catch (e) {
          console.error('Error parsing setting value:', e);
        }
      }

      // Create form controls for each key in the object
      const formControls: Record<string, [unknown]> = {};
      Object.keys(parsedValue).forEach(key => {
        formControls[key] = [parsedValue[key] ?? ''];
      });

      this.formGroup = this.fb.group(formControls);
    }
  }

  private loadComponent(): void {
    if (!this.componentContainer || !this.componentType) {
      return;
    }

    const componentRef = this.componentContainer.createComponent(
      this.componentType,
    );

    const instance = componentRef.instance as Record<string, unknown>;

    // Type for setInput method (Angular 14+)
    type ComponentRefWithSetInput = {
      setInput: (key: string, value: unknown) => void;
    };

    // Set formGroup input using Angular's setInput if available (Angular 14+)
    // This ensures proper input change detection and triggers ngOnChanges
    // Note: We don't check 'formGroup' in instance because @Input() properties
    // may not exist on the instance object until they're set
    const hasSetInput =
      'setInput' in componentRef &&
      typeof (componentRef as unknown as ComponentRefWithSetInput).setInput ===
        'function';
    if (hasSetInput) {
      (componentRef as unknown as ComponentRefWithSetInput).setInput(
        'formGroup',
        this.formGroup,
      );
    } else {
      // Fallback for older Angular versions - set directly and trigger change detection
      instance['formGroup'] = this.formGroup;
    }

    // Set additional inputs (use setInput when available, same as formGroup above)
    if (this.additionalInputs) {
      Object.keys(this.additionalInputs).forEach(key => {
        if (hasSetInput) {
          (componentRef as unknown as ComponentRefWithSetInput).setInput(
            key,
            this.additionalInputs![key],
          );
        } else if (key in instance) {
          instance[key] = this.additionalInputs![key];
        }
      });
    }

    // Trigger change detection to ensure inputs are processed
    componentRef.changeDetectorRef.detectChanges();
  }

  async onSubmit(): Promise<void> {
    // If component doesn't use formGroup (like fees), it handles submission via callbacks
    // The wrapper's onSubmit is triggered by the form, but components like fees
    // handle submission internally via actions.onSubmit callback
    if (!this.formGroupInitializer) {
      // Component handles its own submission (e.g., fees via actions.onSubmit)
      // Just close the dialog on form submit (component will handle the actual submission)
      // The component should call context.completeWith(true) when done
      return;
    }

    // Standard form submission for components that use formGroup
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.submitting = true;

    const formValue = this.formGroup.value;

    const updatedSetting: RecipientSetting | DomainSetting = {
      ...this.setting,
      value: formValue as Record<string, unknown>,
    };

    this.editSettingFn(this.id, updatedSetting).subscribe({
      next: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateSuccess', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.success'),
              appearance: 'success',
            },
          )
          .subscribe();
        this.context.completeWith(true);
      },
      error: (error: Error) => {
        console.error('Error updating setting:', error);
        this.alerts
          .open(
            this.translate.instant('features.settings.updateError', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  close(): void {
    this.context.completeWith(false);
  }

  private getComponentDataForSetting(
    key: string,
    id: number,
    setting: RecipientSetting | DomainSetting,
    editSettingFn: (
      id: number,
      setting: RecipientSetting | DomainSetting,
    ) => Observable<void>,
  ): {
    componentType: Type<unknown>;
    formGroupInitializer?: (
      settingValue: string | Record<string, unknown> | unknown,
      fb: FormBuilder,
    ) => FormGroup;
    additionalInputs?: Record<string, unknown>;
  } {
    // Component-specific data structures
    switch (key) {
      case 'fees': {
        this.showButtons = false;
        // Use wrapper pattern: SettingsDialogComponent wraps GateweeFeesSettingsComponent
        // Fees component uses actions.onSubmit callback for submission, not formGroup
        const feesSnapshot: FeeSnapshot = {domain: [], recipient: []};

        if (setting.value) {
          try {
            const parsed =
              typeof setting.value === 'string'
                ? JSON.parse(setting.value)
                : setting.value;
            feesSnapshot.domain = parsed as FeeSnapshotItem[];
          } catch (e) {
            console.error('Error parsing fees setting:', e);
          }
        }

        // Get enabled payment methods from app settings or default
        const enabledPaymentMethods: Record<string, boolean> = {
          pix: true,
          bank_slip: true,
          credit_card: true,
          slipix: true,
        };

        return {
          componentType: GateweeFeesSettingsComponent,
          // No formGroupInitializer - fees component creates its own form internally
          additionalInputs: {
            entity:
              this.entity ||
              ({
                id,
                name: '',
                document: '',
              } as FeeEntity),
            fees: feesSnapshot,
            enabledPaymentMethods,
            scope: this.scope || 'domain',
            defaultFees: null,
            disableContentPage: true, // Always disable content page in dialog
            actions: {
              onSubmit: (fees: FeeSnapshotItem[]) => {
                // Update fees snapshot with new fees
                const updatedFeesSnapshot: FeeSnapshot = {...feesSnapshot};
                if ((this.scope || 'domain') === 'domain') {
                  updatedFeesSnapshot.domain = fees;
                } else {
                  updatedFeesSnapshot.recipient = fees;
                }

                const updatedSetting: RecipientSetting | DomainSetting = {
                  ...setting,
                  value: updatedFeesSnapshot as unknown as
                    | string
                    | Record<string, unknown>,
                };

                // Subscribe to editSettingFn and handle dialog closing
                editSettingFn(id, updatedSetting).subscribe({
                  next: () => {
                    this.alerts
                      .open(
                        this.translate.instant(
                          'features.settings.updateSuccess',
                          {
                            key: setting.key,
                          },
                        ),
                        {
                          label: this.translate.instant(
                            'common.status.success',
                          ),
                          appearance: 'success',
                        },
                      )
                      .subscribe();
                    this.context.completeWith(true);
                  },
                  error: (error: Error) => {
                    console.error('Error updating setting:', error);
                    this.alerts
                      .open(
                        this.translate.instant(
                          'features.settings.updateError',
                          {
                            key: setting.key,
                          },
                        ),
                        {
                          label: this.translate.instant('common.status.error'),
                          appearance: 'error',
                        },
                      )
                      .subscribe();
                  },
                });
              },
            },
          },
        };
      }

      case 'style': {
        // Use wrapper pattern: SettingsDialogComponent wraps GateweeStyleSettingsComponent
        return {
          componentType: GateweeStyleSettingsComponent,
          additionalInputs: {
            domainId: id,
          },
          formGroupInitializer: (
            value: string | Record<string, unknown> | unknown,
            fb: FormBuilder,
          ) => {
            // Parse setting value and create FormGroup for style settings
            let parsedValue: Record<string, unknown> = {
              primaryColor: '#0066CC',
              secondaryColor: '#003366',
              accentColor: '#0099FF',
              logo: '',
              logoWhite: '',
              logoBlack: '',
              logoSymbol: '',
              favicon: '',
              theme: 'light',
            };

            if (value) {
              try {
                const parsed =
                  typeof value === 'string' ? JSON.parse(value) : value;
                parsedValue = {
                  ...parsedValue,
                  ...(parsed as Record<string, unknown>),
                };
              } catch (e) {
                console.error('Error parsing style setting:', e);
              }
            }

            return fb.group({
              primaryColor: [parsedValue['primaryColor'] || '#0066CC'],
              secondaryColor: [parsedValue['secondaryColor'] || '#003366'],
              accentColor: [parsedValue['accentColor'] || '#0099FF'],
              logo: [parsedValue['logo'] || ''],
              logoWhite: [parsedValue['logoWhite'] || ''],
              logoBlack: [parsedValue['logoBlack'] || ''],
              logoSymbol: [parsedValue['logoSymbol'] || ''],
              favicon: [parsedValue['favicon'] || ''],
              theme: [parsedValue['theme'] || 'light'],
            });
          },
        };
      }

      case 'page': {
        // Use wrapper pattern: SettingsDialogComponent wraps GateweePageSettingsComponent
        return {
          componentType: GateweePageSettingsComponent,
          additionalInputs: {
            domainId: id,
          },
          formGroupInitializer: (
            value: string | Record<string, unknown> | unknown,
            fb: FormBuilder,
          ) => {
            // Parse setting value and create FormGroup for page settings
            let parsedValue: Record<string, unknown> = {
              title: '',
              description: '',
              keywords: '',
              googleAnalyticsId: '',
              supportContactEmail: '',
              supportContactWhatsapp: '',
              institutionalWebsiteURL: '',
            };

            let mobileHero = {
              title: '',
              description: '',
              image: '',
            };

            let footerCopyright = '';
            let assetsTermsOfService = '';
            let assetsPrivacyPolicy = '';
            let onboardingTitle = '';
            let onboardingDescription = '';
            let sidePanel = {
              backgroundImage: '',
              headerTitle: '',
              footerTitle: '',
              footerDescription: '',
            };
            let checkoutFooterDisclaimer = '';
            let checkoutFooterCopyright = '';

            let loginSidePanelBg = '';
            let loginHeadline = '';
            let loginSubheadline = '';
            let loginStatsRows: Array<{value: string; label: string}> = [];
            let loginAppStoreUrl = '';
            let loginGooglePlayUrl = '';

            if (value) {
              try {
                const parsed =
                  typeof value === 'string' ? JSON.parse(value) : value;
                const record = parsed as Record<string, unknown>;
                parsedValue = {...parsedValue, ...record};
                const mobile = record['mobile'] as
                  | {
                      hero?: {
                        title?: string;
                        description?: string;
                        image?: string;
                      };
                    }
                  | undefined;
                if (mobile?.hero) {
                  mobileHero = {
                    title: mobile.hero.title ?? '',
                    description: mobile.hero.description ?? '',
                    image: mobile.hero.image ?? '',
                  };
                }

                const footer = record['footer'] as
                  | {copyright?: string}
                  | undefined;
                if (footer) {
                  footerCopyright = footer.copyright ?? '';
                }

                const assets = record['assets'] as
                  | {
                      termsOfService?: string;
                      privacyPolicy?: string;
                    }
                  | undefined;
                if (assets) {
                  assetsTermsOfService = assets.termsOfService ?? '';
                  assetsPrivacyPolicy = assets.privacyPolicy ?? '';
                }

                const onboarding = record['onboarding'] as
                  | {
                      title?: string;
                      description?: string;
                      sidePanel?: {
                        backgroundImage?: string;
                        headerTitle?: string;
                        footerTitle?: string;
                        footerDescription?: string;
                      };
                    }
                  | undefined;
                if (onboarding) {
                  onboardingTitle = onboarding.title ?? '';
                  onboardingDescription = onboarding.description ?? '';
                  if (onboarding.sidePanel) {
                    sidePanel = {
                      backgroundImage:
                        onboarding.sidePanel.backgroundImage ?? '',
                      headerTitle: onboarding.sidePanel.headerTitle ?? '',
                      footerTitle: onboarding.sidePanel.footerTitle ?? '',
                      footerDescription:
                        onboarding.sidePanel.footerDescription ?? '',
                    };
                  }
                }

                const checkout = record['checkout'] as
                  | {
                      footer?: {
                        disclaimer?: string;
                        copyright?: string;
                      };
                    }
                  | undefined;
                if (checkout?.footer) {
                  checkoutFooterDisclaimer = checkout.footer.disclaimer ?? '';
                  checkoutFooterCopyright = checkout.footer.copyright ?? '';
                }

                const loginBlock = record['login'] as
                  | {
                      sidePanel?: {
                        backgroundImage?: string;
                        headline?: string;
                        subheadline?: string;
                        stats?: Array<{value?: string; label?: string}>;
                      };
                      mobileApp?: {
                        appStoreUrl?: string;
                        googlePlayUrl?: string;
                      };
                    }
                  | undefined;
                if (loginBlock?.sidePanel) {
                  loginSidePanelBg = loginBlock.sidePanel.backgroundImage ?? '';
                  loginHeadline = loginBlock.sidePanel.headline ?? '';
                  loginSubheadline = loginBlock.sidePanel.subheadline ?? '';
                  const rawStats = loginBlock.sidePanel.stats;
                  if (Array.isArray(rawStats)) {
                    loginStatsRows = rawStats.map(s => ({
                      value: s?.value ?? '',
                      label: s?.label ?? '',
                    }));
                  }
                }
                if (loginBlock?.mobileApp) {
                  loginAppStoreUrl = loginBlock.mobileApp.appStoreUrl ?? '';
                  loginGooglePlayUrl = loginBlock.mobileApp.googlePlayUrl ?? '';
                }
              } catch (e) {
                console.error('Error parsing page setting:', e);
              }
            }

            const loginStatsFormArray = fb.array<FormGroup>(
              loginStatsRows.length
                ? loginStatsRows.map(row =>
                    fb.group({
                      value: [row.value],
                      label: [row.label],
                    }),
                  )
                : [],
            );

            return fb.group({
              title: [parsedValue['title'] || ''],
              description: [parsedValue['description'] || ''],
              keywords: [parsedValue['keywords'] || ''],
              googleAnalyticsId: [parsedValue['googleAnalyticsId'] || ''],
              supportContactEmail: [parsedValue['supportContactEmail'] || ''],
              supportContactWhatsapp: [
                parsedValue['supportContactWhatsapp'] || '',
              ],
              institutionalWebsiteURL: [
                parsedValue['institutionalWebsiteURL'] || '',
              ],
              footer: fb.group({
                copyright: [footerCopyright],
              }),
              assets: fb.group({
                termsOfService: [assetsTermsOfService],
                privacyPolicy: [assetsPrivacyPolicy],
              }),
              mobile: fb.group({
                hero: fb.group({
                  title: [mobileHero.title],
                  description: [mobileHero.description],
                  image: [mobileHero.image],
                }),
              }),
              onboarding: fb.group({
                title: [onboardingTitle],
                description: [onboardingDescription],
                sidePanel: fb.group({
                  backgroundImage: [sidePanel.backgroundImage],
                  headerTitle: [sidePanel.headerTitle],
                  footerTitle: [sidePanel.footerTitle],
                  footerDescription: [sidePanel.footerDescription],
                }),
              }),
              checkout: fb.group({
                footer: fb.group({
                  disclaimer: [checkoutFooterDisclaimer],
                  copyright: [checkoutFooterCopyright],
                }),
              }),
              login: fb.group({
                sidePanel: fb.group({
                  backgroundImage: [loginSidePanelBg],
                  headline: [loginHeadline],
                  subheadline: [loginSubheadline],
                  stats: loginStatsFormArray,
                }),
                mobileApp: fb.group({
                  appStoreUrl: [loginAppStoreUrl],
                  googlePlayUrl: [loginGooglePlayUrl],
                }),
              }),
            });
          },
        };
      }

      default:
        // Default: return empty (should not be called for unknown keys)
        throw new Error(`Unknown setting key: ${key}`);
    }
  }
}
